import { z } from 'zod'
import { surTiime } from './acheminement'
import { ajouterJours } from './calendrier'
import { formaterJour } from './dates'
import {
  DEVISE_PAR_DEFAUT,
  type ModeTransmission,
  type OrigineClassement,
  type OrigineRefacturation,
  type StatutPaiement,
} from './domaine'
import { formaterMontant } from './montant'
import { type DossierPlan, planActif, recoitFichiers } from './plan'

// Frais à refacturer : une dépense réglée par une de nos entités (le foyer, une société) que doit
// rembourser une autre de nos entités ou un tiers (employeur, locataire). Deux sens pour la
// pièce : elle reste chez qui a réglé (refacturation, note de frais d'un employeur), ou elle est
// chez l'entité qui rembourse, dont elle est la charge (avance du foyer à une société, à mettre
// dans sa compta). Une dépense sans pièce (indemnités kilométriques, facture pas encore reçue)
// se note avec un libellé et une date. Chaque dépense donne une ligne ; on se rembourse par
// lot, d'un coup : un PDF (récapitulatif puis les pièces), un seul encaissement attendu, une
// tâche et, au besoin, un courriel préparé (N1 : rien ne part de l'appli). Fonctions pures ;
// les services appliquent.

const id = z.uuid()
const jour = z.iso.date()
const centimes = z.number().int().positive()
const motif = z.string().trim().min(1).max(500)
const libelle = z.string().trim().min(1).max(300)

/** Qui rembourse : une de nos entités, ou un tiers ; jamais les deux. */
export type Destinataire = {
  destinataireEntiteId: string | null
  destinataireTiersId: string | null
}

const destinataire = {
  destinataireEntiteId: id.nullable().optional(),
  destinataireTiersId: id.nullable().optional(),
}

const UN_SEUL_DESTINATAIRE = 'Désigner qui rembourse : une entité ou un tiers, l’un ou l’autre.'

function unSeulDestinataire(d: {
  destinataireEntiteId?: string | null
  destinataireTiersId?: string | null
}): boolean {
  return !!d.destinataireEntiteId !== !!d.destinataireTiersId
}

// Saisie

/**
 * Une dépense à se faire rembourser. Sur une pièce : par défaut tout ce qui en reste (TTC moins
 * les autres lignes) ; qui a réglé (`entiteId`), par défaut l'entité de la pièce ; une avance
 * le précise (le foyer), la pièce étant chez l'entité qui rembourse. Sans pièce : qui a réglé,
 * libellé, date et montant, le justificatif se joint ensuite.
 */
export const ligneRefacturationCreation = z
  .object({
    pieceId: id.optional(),
    entiteId: id.optional(),
    ...destinataire,
    libelle: libelle.optional(),
    date: jour.optional(),
    montantCts: centimes.optional(),
    motif: motif.nullable().optional(),
  })
  .refine(unSeulDestinataire, { message: UN_SEUL_DESTINATAIRE, path: ['destinataireEntiteId'] })
  .refine(
    (d) =>
      d.pieceId !== undefined ||
      (d.entiteId !== undefined &&
        d.libelle !== undefined &&
        d.date !== undefined &&
        d.montantCts !== undefined),
    {
      message: 'Sans pièce : préciser qui a réglé (entiteId), le libellé, la date et le montant.',
      path: ['pieceId'],
    },
  )
export type LigneRefacturationCreation = z.infer<typeof ligneRefacturationCreation>

/** Retoucher une ligne ; `pieceId` joint le justificatif d'une dépense notée sans pièce. */
export const ligneRefacturationModification = z
  .object({
    montantCts: centimes.optional(),
    motif: motif.nullable().optional(),
    libelle: libelle.optional(),
    date: jour.optional(),
    pieceId: id.optional(),
  })
  .refine((d) => Object.values(d).some((v) => v !== undefined), 'Rien à modifier.')
export type LigneRefacturationModification = z.infer<typeof ligneRefacturationModification>

/** Abandonner une ligne (rien à refacturer, déjà remboursée ailleurs) ; la rouvrir. */
export const ACTIONS_LIGNE_REFACTURATION = ['abandonner', 'rouvrir'] as const
export type ActionLigneRefacturation = (typeof ACTIONS_LIGNE_REFACTURATION)[number]
export const actionLigneRefacturation = z.object({
  action: z.enum(ACTIONS_LIGNE_REFACTURATION),
  motif: motif.optional(),
})

/** Lot : les lignes choisies, toutes du même couple (qui a réglé, qui rembourse). */
export const lotRefacturationPreparation = z.object({
  ligneIds: z.array(id).min(1).max(100),
})
export type LotRefacturationPreparation = z.infer<typeof lotRefacturationPreparation>

/** Quote-part en pour cent (52,45), au centième près. */
const quotePart = z
  .number()
  .gt(0)
  .max(100)
  .refine((p) => Math.abs(p * 100 - Math.round(p * 100)) < 1e-6, 'Quote-part au centième près.')

/**
 * Règle d'un dossier du plan : toute pièce qui y est classée, datée à partir de `depuis`, est à
 * refacturer à ce destinataire (notes de frais remboursées par un employeur), en entier ou pour
 * sa quote-part (taxe foncière d'un immeuble refacturée au locataire pour sa surface). Null :
 * aucune.
 */
export const refacturationDossier = z
  .object({ ...destinataire, depuis: jour, quotePart: quotePart.optional() })
  .refine(unSeulDestinataire, { message: UN_SEUL_DESTINATAIRE, path: ['destinataireEntiteId'] })
  .nullable()
export type RefacturationDossier = z.infer<typeof refacturationDossier>

// États, calculés (jamais stockés)

/**
 * Où en est une ligne : à refacturer (dans aucun lot), refacturée (dans un lot, remboursement
 * attendu), remboursée (l'encaissement du lot est payé ou rapproché), abandonnée.
 */
export const ETATS_LIGNE_REFACTURATION = [
  'a_refacturer',
  'refacturee',
  'remboursee',
  'abandonnee',
] as const
export type EtatLigneRefacturation = (typeof ETATS_LIGNE_REFACTURATION)[number]

/** Lot : remboursement attendu, remboursé (encaissement payé ou rapproché), ou annulé. */
export const ETATS_LOT_REFACTURATION = ['attendu', 'rembourse', 'annule'] as const
export type EtatLotRefacturation = (typeof ETATS_LOT_REFACTURATION)[number]

const ENCAISSE: readonly StatutPaiement[] = ['paye', 'rapproche']

export function etatLot(l: {
  annule: boolean
  paiementStatut: StatutPaiement | null
}): EtatLotRefacturation {
  if (l.annule) return 'annule'
  return l.paiementStatut && ENCAISSE.includes(l.paiementStatut) ? 'rembourse' : 'attendu'
}

export function etatLigne(l: {
  abandonnee: boolean
  lot: { annule: boolean; paiementStatut: StatutPaiement | null } | null
}): EtatLigneRefacturation {
  if (l.abandonnee) return 'abandonnee'
  if (!l.lot) return 'a_refacturer'
  const lot = etatLot(l.lot)
  if (lot === 'annule') return 'a_refacturer'
  return lot === 'rembourse' ? 'remboursee' : 'refacturee'
}

/** Une ligne se retouche ou s'abandonne tant qu'elle n'est dans aucun lot. */
export function ligneModifiable(etat: EtatLigneRefacturation): boolean {
  return etat === 'a_refacturer'
}

// Montants

/**
 * Ce qui reste à refacturer sur une pièce : son total (TTC, sinon le montant à payer) moins les
 * lignes déjà posées et non abandonnées. Null : montant de la pièce inconnu.
 */
export function resteRefacturable(e: { ttcCts: number | null; dejaCts: number }): number | null {
  if (e.ttcCts === null) return null
  return Math.max(0, e.ttcCts - e.dejaCts)
}

/** Message d'erreur si le montant ne convient pas, sinon null. */
export function erreurMontantLigne(
  montantCts: number | undefined,
  e: { ttcCts: number | null; dejaCts: number; devise: string },
): string | null {
  const reste = resteRefacturable(e)
  if (reste === 0) return 'Rien à refacturer : tout le montant de la pièce l’est déjà.'
  if (montantCts === undefined) {
    return reste === null ? 'Montant de la pièce inconnu : préciser le montant à refacturer.' : null
  }
  if (reste !== null && montantCts > reste)
    return `Au plus ${formaterMontant(reste, e.devise)} à refacturer sur cette pièce.`
  return null
}

// Regroupement et lot

export type LigneAGrouper = Destinataire & {
  id: string
  entiteId: string
  montantCts: number
  devise: string
}

/** Clé d'un couple (qui a réglé, qui rembourse) dans une devise : un lot n'en mélange pas deux. */
export function cleCouple(l: Destinataire & { entiteId: string; devise: string }): string {
  return [
    l.entiteId,
    l.destinataireEntiteId ? `e:${l.destinataireEntiteId}` : `t:${l.destinataireTiersId}`,
    l.devise,
  ].join('|')
}

export type GroupeRefacturation<T extends LigneAGrouper> = Destinataire & {
  cle: string
  entiteId: string
  devise: string
  totalCts: number
  lignes: T[]
}

/** Lignes regroupées par couple, dans l'ordre de leur première apparition, avec leur total. */
export function regrouperLignes<T extends LigneAGrouper>(
  lignes: readonly T[],
): GroupeRefacturation<T>[] {
  const groupes = new Map<string, GroupeRefacturation<T>>()
  for (const l of lignes) {
    const cle = cleCouple(l)
    const g = groupes.get(cle) ?? {
      cle,
      entiteId: l.entiteId,
      destinataireEntiteId: l.destinataireEntiteId,
      destinataireTiersId: l.destinataireTiersId,
      devise: l.devise,
      totalCts: 0,
      lignes: [],
    }
    g.totalCts += l.montantCts
    g.lignes.push(l)
    groupes.set(cle, g)
  }
  return [...groupes.values()]
}

/** Message d'erreur si ces lignes ne font pas un lot, sinon null. */
export function erreurLot(
  lignes: readonly (LigneAGrouper & { etat: EtatLigneRefacturation })[],
): string | null {
  if (lignes.length === 0) return 'Aucune ligne à refacturer.'
  const pasPretes = lignes.filter((l) => l.etat !== 'a_refacturer')
  if (pasPretes.length > 0)
    return `${pasPretes.length} ligne(s) déjà dans un lot ou abandonnée(s) : les retirer.`
  if (new Set(lignes.map(cleCouple)).size > 1)
    return 'Un lot réunit les frais d’une même entité pour un même destinataire, dans une même devise.'
  return null
}

/** Délai laissé pour rembourser un lot (date prévue de l'encaissement). */
export const DELAI_REMBOURSEMENT_JOURS = 30

export function datePrevueRemboursement(aujourdhui: string): string {
  return ajouterJours(aujourdhui, DELAI_REMBOURSEMENT_JOURS)
}

/** Ce que le récapitulatif et le courriel disent d'une ligne. */
export type LigneDuLot = {
  date: string | null
  fournisseur: string | null
  objet: string | null
  montantCts: number
  motif: string | null
  /** Dépense notée sans pièce : son justificatif reste à joindre. */
  sansJustificatif?: boolean
}

function decrire(l: LigneDuLot, devise: string): string {
  return [
    l.date ? formaterJour(l.date) : 'date inconnue',
    ' : ',
    [l.fournisseur, l.objet].filter(Boolean).join(', ') || 'pièce',
    ` ; ${formaterMontant(l.montantCts, devise)}`,
    l.motif ? ` (${l.motif})` : '',
    l.sansJustificatif ? ' [justificatif à joindre]' : '',
    '.',
  ].join('')
}

/** Lignes dans l'ordre des dates (inconnues à la fin). */
export function trierLignesDuLot<T extends { date: string | null }>(lignes: readonly T[]): T[] {
  return [...lignes].sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999'))
}

/** Titre et lignes de la page de récapitulatif du PDF. */
export function recapitulatifLot(e: {
  payeur: string
  destinataire: string
  lignes: readonly LigneDuLot[]
  devise: string
}): { titre: string; lignes: string[]; totalCts: number } {
  const totalCts = e.lignes.reduce((n, l) => n + l.montantCts, 0)
  return {
    titre: `Frais avancés par ${e.payeur}, à rembourser par ${e.destinataire}`,
    lignes: [
      ...trierLignesDuLot(e.lignes).map((l) => decrire(l, e.devise)),
      `Total à rembourser : ${formaterMontant(totalCts, e.devise)} (${e.lignes.length} pièce${e.lignes.length > 1 ? 's' : ''}).`,
    ],
    totalCts,
  }
}

/**
 * Courriel qui transmet le lot : à la comptabilité du destinataire quand c'est une de nos
 * entités, sinon au tiers. Sobre, montants exacts, sans engagement ; le PDF se joint à la main.
 */
export function courrielLot(e: {
  payeur: string
  destinataire: string
  /** Le destinataire est une de nos entités : le courriel va à sa comptabilité. */
  pourComptabilite: boolean
  lignes: readonly LigneDuLot[]
  nomPiece: string
  devise?: string
  manquantes: readonly string[]
}): { objet: string; corps: string; totalCts: number } {
  const devise = e.devise ?? DEVISE_PAR_DEFAUT
  const totalCts = e.lignes.reduce((n, l) => n + l.montantCts, 0)
  const corps = [
    'Bonjour,',
    '',
    e.pourComptabilite
      ? `Voici les frais avancés par ${e.payeur} pour le compte de ${e.destinataire}, à lui refacturer :`
      : `Voici les frais avancés par ${e.payeur} pour votre compte, à nous rembourser :`,
    ...trierLignesDuLot(e.lignes).map((l) => `- ${decrire(l, devise)}`),
    '',
    `Total : ${formaterMontant(totalCts, devise)}.`,
    '',
    `Le dossier joint (${e.nomPiece}) réunit le récapitulatif puis chaque justificatif.`,
    e.manquantes.length > 0
      ? `[à compléter : justificatifs absents du dossier : ${e.manquantes.join(', ')}]`
      : null,
    '',
    'Merci,',
  ]
    .filter((l) => l !== null)
    .join('\n')
  return {
    objet: `Frais à rembourser par ${e.destinataire} (${formaterMontant(totalCts, devise)})`,
    corps,
    totalCts,
  }
}

/** Nom du PDF du lot : « 2026-11-05_Frais a rembourser_Foyer pour Salesforce_123,45EUR.pdf ». */
export function nomFichierLot(e: {
  aujourdhui: string
  payeur: string
  destinataire: string
  totalCts: number
  devise: string
}): string {
  const propre = (t: string) =>
    t
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/[^\p{L}\p{N} -]/gu, '')
      .replace(/\s+/g, ' ')
      .trim()
  const montant = `${centimesEnTexte(e.totalCts)}${e.devise}`
  return `${e.aujourdhui}_Frais a rembourser_${propre(e.payeur)} pour ${propre(e.destinataire)}_${montant}.pdf`
}

/** « 123,45 », sans devise ni séparateur de milliers : lu comme un nombre par Excel en français. */
const centimesEnTexte = (cts: number) =>
  `${Math.floor(cts / 100)},${String(cts % 100).padStart(2, '0')}`

const EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/heic': 'heic',
  'image/webp': 'webp',
}

/**
 * Nom d'un justificatif dans le zip du lot : son rang dans l'ordre des dates, puis le nom de la
 * pièce, avec l'extension de son type (« 01_2026-10-14_Trainline_470,00EUR.pdf »).
 */
export function nomJustificatifDuLot(rang: number, nom: string | null, typeMime: string): string {
  const extension = EXTENSIONS[typeMime]
  const base = (nom ?? 'Piece').replace(/[/\\:]/g, '-').replace(/\.[a-z0-9]{2,4}$/i, '')
  return `${String(rang).padStart(2, '0')}_${base}${extension ? `.${extension}` : ''}`
}

const champCsv = (t: string) => (/[;"\r\n]/.test(t) ? `"${t.replaceAll('"', '""')}"` : t)

/**
 * Récapitulatif du lot pour un outil de notes de frais ou un tableur : une ligne par dépense dans
 * l'ordre des dates, avec le fichier qui la justifie dans le zip, puis le total. Pour Excel en
 * français : point-virgule, virgule décimale, BOM, fins de ligne CRLF.
 */
export function recapitulatifLotCsv(e: {
  lignes: readonly (LigneDuLot & { fichier: string | null })[]
  devise: string
}): string {
  const totalCts = e.lignes.reduce((n, l) => n + l.montantCts, 0)
  const rangees = [
    ['Date', 'Fournisseur', 'Objet', `Montant (${e.devise})`, 'Motif', 'Fichier'],
    ...trierLignesDuLot(e.lignes).map((l) => [
      l.date ? formaterJour(l.date) : '',
      l.fournisseur ?? '',
      l.objet ?? '',
      centimesEnTexte(l.montantCts),
      l.motif ?? '',
      l.fichier ?? (l.sansJustificatif ? 'justificatif à joindre' : 'fichier illisible'),
    ]),
    ['', '', 'Total', centimesEnTexte(totalCts), '', ''],
  ]
  return `\ufeff${rangees.map((r) => r.map(champCsv).join(';')).join('\r\n')}\r\n`
}

/**
 * Comment le lot se transmet. À une de nos entités sur Tiime (collecte par mail ou dépôt
 * direct) : la note de frais se crée dans Tiime avec le PDF, ses pièces y arrivent comme frais
 * avancés, jamais comme des achats qui attendraient un prélèvement ; pas de courriel. À une autre
 * de nos entités : courriel à son cabinet si son adresse est connue. À un tiers : courriel s'il a
 * une adresse, sinon à transmettre à la main (l'outil de notes de frais d'un employeur, par
 * exemple).
 */
export function transmissionLot(e: {
  payeur: string
  destinataire: string
  totalCts: number
  devise: string
  nomPiece: string
  /** Le destinataire est une de nos entités : `transmission` de sa fiche, sinon null. */
  entite: { transmission: ModeTransmission } | null
  /** Adresse mail connue (cabinet de l'entité, ou le tiers), et à qui elle est. */
  adresse: { mail: string; nom: string } | null
}): { titre: string; consigne: string; courriel: boolean } {
  const total = formaterMontant(e.totalCts, e.devise)
  if (e.entite && surTiime(e.entite.transmission))
    return {
      titre: `Créer dans Tiime la note de frais de ${e.destinataire} : ${e.payeur}, ${total}`,
      consigne: `Dans le Tiime de ${e.destinataire} : Dépenses, Note de frais, Créer, au nom de qui a avancé, ${total}, avec « ${e.nomPiece} » (le récapitulatif puis chaque justificatif). Les pièces arrivent ainsi dans la compta comme frais avancés.`,
      courriel: false,
    }
  if (e.adresse)
    return {
      titre: `Transmettre à ${e.adresse.nom} les frais à rembourser par ${e.destinataire} (${total})`,
      consigne: `Courriel préparé dans la boîte d’envoi pour ${e.adresse.nom}, à relire puis envoyer de sa messagerie avec « ${e.nomPiece} » en pièce jointe.`,
      courriel: true,
    }
  return {
    titre: `Transmettre à ${e.destinataire} les frais à rembourser (${total})`,
    consigne: e.entite
      ? `Aucune adresse connue pour la comptabilité de ${e.destinataire} : lui transmettre « ${e.nomPiece} » à la main.`
      : `Transmettre « ${e.nomPiece} » à ${e.destinataire} à la main (son outil de notes de frais, par exemple).`,
    courriel: false,
  }
}

// Règle d'un dossier

/**
 * Une pièce classée dans un dossier qui a une règle de refacturation donne une ligne : datée à
 * partir de `depuis` (sinon le jour de sa réception), d'un montant connu et non nul.
 */
/** Quote-part stockée en centièmes de pour cent (52,45 % : 5 245), lue en pour cent. */
export const quotePartStockee = (p: number | null | undefined) =>
  p === null || p === undefined ? null : Math.round(p * 100)
export const quotePartLue = (centiemes: number | null) =>
  centiemes === null ? null : centiemes / 100

/** « 52,45 % », « 100 % ». */
export const formaterQuotePart = (p: number) =>
  `${p.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}\u00a0%`

/**
 * Montant de la ligne posée par la règle d'un dossier : le total de la pièce, ou sa quote-part
 * arrondie au centime.
 */
export function montantLigneDossier(totalCts: number, quotePartPct: number | null): number {
  const centiemes = quotePartStockee(quotePartPct)
  if (centiemes === null || centiemes >= 10_000) return totalCts
  return Math.round((totalCts * centiemes) / 10_000)
}

export function ligneDuDossier(
  piece: { dateDocument: string | null; recueLe: string; ttcCts: number | null },
  regle: { depuis: string },
): boolean {
  const date = piece.dateDocument ?? piece.recueLe
  return date >= regle.depuis && piece.ttcCts !== null && piece.ttcCts > 0
}

// Classement par la ligne : le sens inverse de la règle d'un dossier

/** Un dossier du plan et sa règle de refacturation. */
export type DossierARegle = DossierPlan & {
  refacturerAEntiteId: string | null
  refacturerATiersId: string | null
  refacturerDepuis: string | null
}

/** Une ligne à refacturer de la pièce ; `creeLe` : l'instant de la décision (ISO). */
export type LigneDeLaPiece = Destinataire & {
  id: string
  entiteId: string
  origine: OrigineRefacturation
  abandonnee: boolean
  creeLe: string
}

export type CibleRefacturation =
  | { nature: 'dossier'; dossierId: string; ligneId: string; decideLe: string }
  /** Aucune ligne vivante, hors celles d'une règle de dossier, de l'entité de la pièce. */
  | { nature: 'sans_ligne' }
  /** Lignes vivantes vers plusieurs destinataires : on ne choisit pas. */
  | { nature: 'destinataires' }
  | { nature: 'aucun' }
  | { nature: 'plusieurs'; dossierIds: string[] }

const cleDestinataire = (d: Destinataire) =>
  d.destinataireEntiteId ? `e:${d.destinataireEntiteId}` : `t:${d.destinataireTiersId}`

/**
 * Le dossier où classer une pièce d'après sa ligne à refacturer (posée à la main, par la
 * décision « qui a payé » ou par une avance) : le seul dossier en service du plan de son entité,
 * qui reçoit des fichiers, dont la règle vise le même destinataire et date d'avant la pièce (date
 * du document, sinon jour de réception, comme `ligneDuDossier`). Seules comptent les lignes
 * vivantes de l'entité de la pièce, qui a réglé : le plan est le sien.
 */
export function cibleDeRefacturation(e: {
  piece: { entiteId: string; dateDocument: string | null; recueLe: string }
  lignes: readonly LigneDeLaPiece[]
  plan: readonly DossierARegle[]
}): CibleRefacturation {
  const vivantes = e.lignes.filter(
    (l) => !l.abandonnee && l.origine !== 'dossier' && l.entiteId === e.piece.entiteId,
  )
  if (vivantes.length === 0) return { nature: 'sans_ligne' }
  const cle = cleDestinataire(vivantes[0]!)
  if (vivantes.some((l) => cleDestinataire(l) !== cle)) return { nature: 'destinataires' }
  const date = e.piece.dateDocument ?? e.piece.recueLe
  const actif = planActif(e.plan)
  const dossierIds = actif
    .filter(
      (d) =>
        d.refacturerDepuis !== null &&
        d.refacturerDepuis <= date &&
        cleDestinataire({
          destinataireEntiteId: d.refacturerAEntiteId,
          destinataireTiersId: d.refacturerATiersId,
        }) === cle &&
        recoitFichiers(actif, d.id),
    )
    .map((d) => d.id)
  if (dossierIds.length === 0) return { nature: 'aucun' }
  if (dossierIds.length > 1) return { nature: 'plusieurs', dossierIds }
  const derniere = vivantes.reduce((a, b) => (Date.parse(b.creeLe) > Date.parse(a.creeLe) ? b : a))
  return {
    nature: 'dossier',
    dossierId: dossierIds[0]!,
    ligneId: derniere.id,
    decideLe: derniere.creeLe,
  }
}

/** Le classement actuel d'une pièce : son dossier, qui l'y a mise et quand (ISO). */
export type ClassementActuel = {
  dossierId: string | null
  origine: OrigineClassement | null
  le: string | null
}

export type EffetClassementRefacturation =
  | { effet: 'rien' }
  | { effet: 'classer'; dossierId: string; ligneId: string }
  /** Déjà classée par la règle là où elle va : une décision « Classer » restée ouverte se ferme. */
  | { effet: 'deja_classee' }
  /** Classée par la règle, qui ne la mène plus nulle part : un humain la classe. */
  | { effet: 'a_classer' }

/**
 * Ce que la ligne fait de la pièce. `decision` : la ligne vient d'être posée, jointe à la pièce
 * ou rouverte, c'est le geste le plus récent ; `reevaluation` (montant changé, rattrapage) : un
 * humain qui a classé la pièce après la décision garde son choix. Sans cible, seule une pièce
 * que la règle avait classée est remise à un humain ; une autre ne bouge pas.
 */
export function classementParRefacturation(e: {
  cible: CibleRefacturation
  actuel: ClassementActuel
  declencheur: 'decision' | 'reevaluation'
}): EffetClassementRefacturation {
  const { cible, actuel } = e
  if (cible.nature !== 'dossier')
    return actuel.origine === 'refacturation' && actuel.dossierId !== null
      ? { effet: 'a_classer' }
      : { effet: 'rien' }
  if (actuel.dossierId === cible.dossierId)
    return actuel.origine === 'refacturation' ? { effet: 'deja_classee' } : { effet: 'rien' }
  if (
    e.declencheur === 'reevaluation' &&
    actuel.dossierId !== null &&
    actuel.origine === 'humain' &&
    actuel.le !== null &&
    Date.parse(actuel.le) > Date.parse(cible.decideLe)
  )
    return { effet: 'rien' }
  return { effet: 'classer', dossierId: cible.dossierId, ligneId: cible.ligneId }
}
